import { useTranslation } from 'react-i18next';
import { Input, SearchableSelect } from 'components/core';
import { CHART_TYPE } from '../../../../../constants';

import styles from './VisualizationTabs.module.css';
import { FieldTooltip } from 'molecules/fieldTooltip/FieldTooltip';

interface InfoProps {
  register: any;
  setValue: any;
  formState: any;
  isExisting?: boolean;
  /** The form's current chart type id. */
  chartType?: string;
  /** KPI Builder mode: the chart type is chosen in Additional Config and the code follows the title. */
  builderMode?: boolean;
}

const chartTypes = [
  { id: CHART_TYPE.METRIC,           itemName: 'Metric' },
  { id: CHART_TYPE.BAR,              itemName: 'Vertical Bar Chart' },
  { id: CHART_TYPE.HORIZONTALBAR,    itemName: 'Horizontal Bar Chart' },
  { id: CHART_TYPE.PIE,              itemName: 'Pie Chart' },
  { id: CHART_TYPE.DONUT,            itemName: 'Donut Chart' },
  { id: CHART_TYPE.LINE,             itemName: 'Line Chart' },
  { id: CHART_TYPE.MULTILINE,        itemName: 'Multiline Chart' },
  { id: CHART_TYPE.STACKBAR,         itemName: 'Stacked Bar Chart' },
  { id: CHART_TYPE.MULTIBAR,         itemName: 'Multi Bar Chart' },
  { id: CHART_TYPE.HISTOGRAM,        itemName: 'Histogram' },
  { id: CHART_TYPE.TABLE,            itemName: 'Table Chart' },
  { id: CHART_TYPE.TABLEBAR,         itemName: 'Table + Bar Chart' },
  { id: CHART_TYPE.TABVIEW,          itemName: 'Tab View' },
  { id: CHART_TYPE.SCATTER,          itemName: 'Scatter Plot Chart' },
  { id: CHART_TYPE.BUBBLE,           itemName: 'Bubble Chart' },
  { id: CHART_TYPE.HEATMAP,          itemName: 'Heatmap' },
  { id: CHART_TYPE.FUNNEL,           itemName: 'Funnel Chart' },
  { id: CHART_TYPE.CONVERSIONFUNNEL, itemName: 'Conversion Funnel Chart' },
  { id: CHART_TYPE.STAGEFUNNEL,      itemName: 'Stage Funnel Chart' },
  { id: CHART_TYPE.MAP,              itemName: 'Map Chart' },
];

const handleFieldChange = (setValue: any, field: string) => (e: any) => {
  setValue(field, e.target.value, { shouldValidate: true });
};

export const Info = ({ register, setValue, formState, isExisting = false, chartType = '', builderMode = false }: InfoProps) => {
  const { t } = useTranslation();
  const codeLocked = isExisting || builderMode;
  const { errors } = formState;

  return (
    <div className="col-sm-12 col-md-12 col-lg-6 mt-4 mb-3">

      {/* Title — mandatory */}
      <div className="mb-4">
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>
            Title <span className={styles.required}>*</span>
          </span>
          <FieldTooltip
            title="Visualization title"
            description="This is the name that will appear at the top of the chart when it is displayed on the dashboard. Keep it short and descriptive."
            example="Total Registered Students"
          />
        </div>
        <Input
          type="text"
          showDescription={false}
          showTitle={false}
          placeholder="Type here"
          {...register('title')}
          className={`${errors.title ? 'is-invalid' : ''}`}
          onChange={handleFieldChange(setValue, builderMode ? 'title' : 'chartName')}
        />
        <div className="invalid-feedback">{errors.title?.message}</div>
      </div>

      {/* Subtitle — optional */}
      <div className="mb-4">
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>Subtitle</span>
          <FieldTooltip
            title="Subtitle"
            description="A short supporting line that appears below the title. Use this to add context — for example the time period or data source the chart represents."
            example="As of March 2026 · Bihar"
          />
        </div>
        <Input
          type="text"
          showDescription={false}
          showTitle={false}
          placeholder="Type here"
          {...register('subTitle')}
          className={`${errors.subTitle ? 'is-invalid' : ''}`}
          onChange={handleFieldChange(setValue, 'subTitle')}
        />
        <div className="invalid-feedback">{errors.subTitle?.message}</div>
      </div>

      {/* Visualization Code — mandatory */}
      <div className="mb-4">
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>
            Visualization code <span className={styles.required}>*</span>
          </span>
          <FieldTooltip
            title="Visualization code"
            description="A unique internal identifier for this chart. It is used by the system to link this visualization to dashboards and to reference it in drill-down configurations. Once set, do not change it."
            example="aastrikaTotalUserReg"
            note="Use camelCase with no spaces. Changing this after creation will break existing dashboard links."
          />
        </div>
        <Input
          type="text"
          showDescription={false}
          showTitle={false}
          placeholder="e.g. totalRegisteredStudents"
          {...register('visualizationCode')}
          className={`${errors.visualizationCode ? 'is-invalid' : ''}`}
          onChange={handleFieldChange(setValue, 'visualizationCode')}
          disabled={codeLocked}
          style={
            codeLocked
              ? { opacity: 0.6, cursor: 'not-allowed', background: 'var(--bg-surface-secondary)' }
              : {}
          }
        />
        <div className="invalid-feedback">{errors.visualizationCode?.message}</div>
      </div>

      {/* Chart type — mandatory. In builder mode it is chosen in Additional Config. */}
      {!builderMode && (
      <div>
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>
            Chart type <span className={styles.required}>*</span>
          </span>
          <FieldTooltip
            title="Chart type"
            description="Choose how the data should be displayed visually. Use Metric for a single number, Bar/Horizontal Bar for comparisons, Line for trends over time, Pie for proportions, and Table for detailed lists."
            example="Metric — for a single KPI like total count. Bar — for comparing districts or blocks."
          />
        </div>
        <SearchableSelect
          options={chartTypes.map((c) => c.itemName)}
          selected={chartTypes.find((c) => c.id === chartType)?.itemName ?? ''}
          placeholder={t('FIELD.SELECT_CHART_TYPE')}
          disabled={isExisting}
          onChange={(name) => {
            const picked = chartTypes.find((c) => c.itemName === name);
            if (picked) setValue('chartType', picked.id, { shouldValidate: true });
          }}
        />
        <Input type="hidden" {...register('chartType')} />
        <div className="invalid-feedback">{errors.chartType?.message}</div>
      </div>
      )}
    </div>
  );
};